import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { TenantFormData } from "@/types/tenant";
import { Mail, UserPlus } from "lucide-react";

interface AdminInviteStepProps {
  formData: TenantFormData;
  updateFormData: (updates: Partial<TenantFormData>) => void;
  errors: Record<string, string>;
}

export function AdminInviteStep({ formData, updateFormData, errors }: AdminInviteStepProps) {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">Admin Invitation</h2>
        <p className="text-muted-foreground">
          Optionally invite someone from the customer to be an admin
        </p>
      </div>

      <div className="flex items-center justify-between p-4 border rounded-lg">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
            <UserPlus className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="font-medium">Invite a Customer Admin</p>
            <p className="text-sm text-muted-foreground">
              They'll receive an email to set up their admin account
            </p>
          </div>
        </div>
        <Switch
          checked={formData.invite_admin}
          onCheckedChange={(checked) => updateFormData({ invite_admin: checked })}
        />
      </div>

      {formData.invite_admin && (
        <div className="grid gap-4 animate-in fade-in-50 slide-in-from-top-2 duration-200">
          <div className="space-y-2">
            <Label htmlFor="admin_name">Admin Name</Label>
            <Input
              id="admin_name"
              placeholder="Enter admin's name"
              value={formData.admin_name || ""}
              onChange={(e) => updateFormData({ admin_name: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="admin_email">Admin Email *</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="admin_email"
                type="email"
                placeholder="admin@customer.com"
                value={formData.admin_email || ""}
                onChange={(e) => updateFormData({ admin_email: e.target.value })}
                className={`pl-9 ${errors.admin_email ? "border-destructive" : ""}`}
              />
            </div>
            {errors.admin_email && (
              <p className="text-sm text-destructive">{errors.admin_email}</p>
            )}
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-900">
            <p className="text-sm text-blue-700 dark:text-blue-400">
              An invitation email will be sent after the tenant is created. The invitation will expire in 7 days.
            </p>
          </div>
        </div>
      )}

      {!formData.invite_admin && (
        <div className="p-4 bg-muted/50 rounded-lg">
          <p className="text-sm text-muted-foreground">
            You can always invite admins later from the tenant details page.
          </p>
        </div>
      )}
    </div>
  );
}
